import { defineCopy } from '../pageCopy';

/**
 * Copy for /about-datacode (en, ar, ckb). English is the live page text; the
 * only differences from the old inline strings are dash punctuation, which the
 * shared guard test forbids (em dashes became colons or commas).
 */
interface AboutCopy {
  meta: { title: string; description: string; ogDescription: string };
  hero: {
    title: string;
    subtitle: string;
    scrollToExplore: string;
    servicesLink: string;
  };
  services: {
    eyebrow: string;
    titleLine1: string;
    titleLine2: string;
    items: { title: string; description: string }[];
  };
  mission: { eyebrow: string; heading: string; body: string };
  clients: { eyebrow: string; heading: string; empty: string };
  cta: { prompt: string; word: string };
}

const en: AboutCopy = {
  meta: {
    title: 'About Us',
    description:
      'Learn about Datacode - a leading digital agency in Erbil specializing in AI Integration, LLM Solutions, Web Development, and Mobile App Development.',
    ogDescription:
      'Learn about Datacode - a leading digital agency specializing in AI Integration, LLM Solutions, Web Development, and Mobile App Development.',
  },
  hero: {
    title: 'OUR STUDIO',
    subtitle:
      'WE ARE A STUDIO THAT DESIGNS AND BUILDS WEB APPS, AI TOOLS, AND MOBILE PRODUCTS. THE PEOPLE WHO DESIGN IT ARE THE PEOPLE WHO WRITE THE CODE.',
    scrollToExplore: 'Scroll to Explore',
    servicesLink: 'Our Services',
  },
  services: {
    eyebrow: 'What we do',
    titleLine1: 'OUR',
    titleLine2: 'SERVICES',
    items: [
      {
        title: 'Mobile App Development',
        description:
          'You get a real app on the App Store and Google Play: one codebase, both platforms. We build it, ship it, and stay on for the updates after launch.',
      },
      {
        title: 'Web Design & Development',
        description:
          'A website or web app that loads fast, works on every screen, and reads like your brand wrote it. We design and build it, then hand you the keys.',
      },
      {
        title: 'AI Integration',
        description:
          'AI that does a specific job inside your product: answering customers, sorting documents, pulling answers out of your own data. We scope it to one real task and wire it in.',
      },
      {
        title: 'Digital Strategy & Consultation',
        description:
          'You sit down with the engineers who would actually build it. We tell you what to build first, what to skip, and which tech choices you will regret in a year.',
      },
      {
        title: 'UI/UX Design',
        description:
          'Screens people understand without a manual. We map the flows, design the interface, and hand off files your developers can build from, or we build them ourselves.',
      },
      {
        title: 'E-Commerce Solutions',
        description:
          'An online store that takes payment, tracks stock, and shows you what is selling. Built to handle local payment methods, not just the ones that work in Silicon Valley.',
      },
      {
        title: 'Humanitarian & Charity Apps',
        description:
          'Apps for NGOs and charities that work on a tight budget and in places with bad connectivity. We have built for this region, so we know what breaks in the field.',
      },
      {
        title: 'Social Media Management',
        description:
          'We run your social accounts: plan the posts, write them, publish them, so the feed stays alive without you watching it every day.',
      },
      {
        title: 'Content Creation',
        description:
          'Words, images, and video that sound like you and say something. We make the content; you stop staring at a blank page.',
      },
      {
        title: 'Data Analytics',
        description:
          'A dashboard that answers the questions you actually ask (what sold, what stalled, what to do next), pulled live from your own systems.',
      },
    ],
  },
  mission: {
    eyebrow: 'Our Mission',
    heading:
      'DataCode designs and builds web apps, AI tools, and mobile products. Built in Kurdistan, shipped worldwide.',
    body: 'We build web apps, AI tools, and mobile products for clients here in Kurdistan and around the world. We take a project from the first sketch to production, and the people who design it are the people who write the code. No hand-off, no middlemen.',
  },
  clients: {
    eyebrow: 'Our Clients',
    heading: 'We build products our clients are proud to put their name on.',
    empty: 'No clients available',
  },
  cta: {
    prompt: 'Ready to start a project?',
    word: 'CONTACT',
  },
};

const ar: AboutCopy = {
  meta: {
    title: 'من نحن',
    description:
      'تعرّف على Datacode، وكالة رقمية رائدة في أربيل متخصصة بدمج الذكاء الاصطناعي، وحلول LLM، وتطوير الويب، وتطوير تطبيقات الموبايل.',
    ogDescription:
      'تعرّف على Datacode، وكالة رقمية رائدة متخصصة بدمج الذكاء الاصطناعي، وحلول LLM، وتطوير الويب، وتطوير تطبيقات الموبايل.',
  },
  hero: {
    title: 'استوديونا',
    subtitle:
      'نحن استوديو يصمم ويبني تطبيقات ويب، وأدوات ذكاء اصطناعي، ومنتجات موبايل. الناس اللي يصممون هم نفسهم الناس اللي يكتبون الكود.',
    scrollToExplore: 'مرر للأسفل للاستكشاف',
    servicesLink: 'خدماتنا',
  },
  services: {
    eyebrow: 'شنو نسوي',
    titleLine1: 'خدماتنا',
    titleLine2: '',
    items: [
      {
        title: 'تطوير تطبيقات الموبايل',
        description:
          'يصير عندك تطبيق حقيقي على App Store وGoogle Play، بكود واحد لكلا المنصتين. نبنيه ونطلقه، ونظل وياك للتحديثات بعد الإطلاق.',
      },
      {
        title: 'تصميم وتطوير المواقع',
        description:
          'موقع أو تطبيق ويب يفتح بسرعة، يشتغل على كل الشاشات، ويبين وكأن علامتك التجارية هي اللي كتبته. نصممه ونبنيه، وبعدها نسلمك المفاتيح.',
      },
      {
        title: 'دمج الذكاء الاصطناعي',
        description:
          'ذكاء اصطناعي يسوي شغلة محددة داخل منتجك: يجاوب الزبائن، يرتب المستندات، ويطلّع الإجابات من بياناتك. نحدد له مهمة وحدة حقيقية ونربطه بالمنتج.',
      },
      {
        title: 'الاستراتيجية الرقمية والاستشارات',
        description:
          'تجلس مع المهندسين اللي راح يبنون المشروع فعلاً. نكلك شنو تبني أول شي، وشنو تتركه، وأي اختيارات تقنية راح تندم عليها بعد سنة.',
      },
      {
        title: 'تصميم UI/UX',
        description:
          'شاشات يفهمها الناس بدون دليل استخدام. نرسم مسارات الاستخدام، نصمم الواجهة، ونسلمك ملفات يقدر مطوروك يبنون منها، أو نبنيها احنا بنفسنا.',
      },
      {
        title: 'حلول التجارة الإلكترونية',
        description:
          'متجر إلكتروني يستلم الدفع، يتابع المخزون، ويريك شنو اللي ينباع. مبني حتى يشتغل مع طرق الدفع المحلية، مو بس اللي تشتغل بوادي السيليكون.',
      },
      {
        title: 'تطبيقات إنسانية وخيرية',
        description:
          'تطبيقات للمنظمات والجمعيات الخيرية تشتغل بميزانية ضيقة وبأماكن الإنترنت بيها ضعيف. بنينا لهذي المنطقة، فنعرف شنو ينكسر بالميدان.',
      },
      {
        title: 'إدارة وسائل التواصل الاجتماعي',
        description:
          'ندير حساباتك على السوشيال ميديا: نخطط المنشورات، نكتبها، وننشرها، حتى يبقى حسابك حي بدون ما تراقبه كل يوم.',
      },
      {
        title: 'صناعة المحتوى',
        description:
          'كلام وصور وفيديو يشبهونك ويقولون شي مفيد. احنا نسوي المحتوى، وانت تخلص من الصفحة البيضاء.',
      },
      {
        title: 'تحليل البيانات',
        description:
          'لوحة تحكم تجاوب على الأسئلة اللي تسألها فعلاً (شنو انباع، شنو وقف، شنو نسوي بعدين)، مسحوبة مباشرة من أنظمتك.',
      },
    ],
  },
  mission: {
    eyebrow: 'رسالتنا',
    heading:
      'Datacode تصمم وتبني تطبيقات ويب، وأدوات ذكاء اصطناعي، ومنتجات موبايل. نبنيها بكوردستان ونوصلها لكل العالم.',
    body: 'نبني تطبيقات ويب، وأدوات ذكاء اصطناعي، ومنتجات موبايل لعملاء هنا بكوردستان وبكل أنحاء العالم. ناخذ المشروع من أول رسمة لين الإنتاج، والناس اللي يصممونه هم نفسهم اللي يكتبون الكود. بدون تسليم شغل، بدون وسطاء.',
  },
  clients: {
    eyebrow: 'عملاؤنا',
    heading: 'نبني منتجات يفتخر عملاؤنا بوضع اسمهم عليها.',
    empty: 'لا يوجد عملاء حالياً',
  },
  cta: {
    prompt: 'جاهز تبدأ مشروعك؟',
    word: 'تواصل معنا',
  },
};

const ckb: AboutCopy = {
  meta: {
    title: 'دەربارەمان',
    description:
      'Datacode بناسە، ئاژانسێکی دیجیتاڵی پێشەنگە لە هەولێر کە پسپۆڕە لە تێکەڵکردنی زیرەکی دەستکرد، چارەسەری LLM، گەشەپێدانی وێب و گەشەپێدانی ئەپی مۆبایل.',
    ogDescription:
      'Datacode بناسە، ئاژانسێکی دیجیتاڵی پێشەنگ کە پسپۆڕە لە تێکەڵکردنی زیرەکی دەستکرد، چارەسەری LLM، گەشەپێدانی وێب و گەشەپێدانی ئەپی مۆبایل.',
  },
  hero: {
    title: 'ستۆدیۆمان',
    subtitle:
      'ئێمە ستۆدیۆیەکین کە ئەپی وێب، ئامرازی زیرەکی دەستکرد و بەرهەمی مۆبایل دیزاین و دروست دەکەین. ئەو کەسانەی دیزاینەکە دەکەن، هەمان ئەو کەسانەن کە کۆدەکە دەنووسن.',
    scrollToExplore: 'بۆ گەڕان بڕۆ خوارەوە',
    servicesLink: 'خزمەتگوزارییەکانمان',
  },
  services: {
    eyebrow: 'چی دەکەین',
    titleLine1: 'خزمەتەکانمان',
    titleLine2: '',
    items: [
      {
        title: 'گەشەپێدانی ئەپی مۆبایل',
        description:
          'ئەپێکی ڕاستەقینەت دەبێت لەسەر App Store و Google Play، بە یەک کۆد بۆ هەردوو پلاتفۆرم. دروستی دەکەین، بڵاوی دەکەینەوە و دوای دەرچوونیش بۆ نوێکردنەوەکان لەگەڵتدا دەمێنینەوە.',
      },
      {
        title: 'دیزاین و گەشەپێدانی وێب',
        description:
          'ماڵپەڕ یان ئەپی وێبێک کە خێرا دەکرێتەوە، لەسەر هەموو شاشەیەک کار دەکات و وا دەردەکەوێت کە براندەکەت خۆی نووسیبێتی. دیزاین و دروستی دەکەین، پاشان کلیلەکانی دەدەینە دەستت.',
      },
      {
        title: 'تێکەڵکردنی زیرەکی دەستکرد',
        description:
          'زیرەکی دەستکرد کە کارێکی دیاریکراو لەناو بەرهەمەکەتدا دەکات: وەڵامدانەوەی کڕیار، ڕیزکردنی بەڵگەنامە، دەرهێنانی وەڵام لە داتاکانی خۆت. بۆ یەک کاری ڕاستەقینە دیاری دەکەین و دەیبەستینەوە بە بەرهەمەکەت.',
      },
      {
        title: 'ستراتیژی دیجیتاڵ و ڕاوێژکاری',
        description:
          'لەگەڵ ئەو ئەندازیارانە دادەنیشیت کە بەڕاستی دروستی دەکەن. پێت دەڵێین یەکەم شت چی دروست بکەیت، چی بهێڵیتەوە، و کام هەڵبژاردنی تەکنیکی دوای ساڵێک پەشیمانت دەکاتەوە.',
      },
      {
        title: 'دیزاینی UI/UX',
        description:
          'شاشەی وا کە خەڵک بەبێ ڕێنمایی تێی دەگات. ڕێڕەوی بەکارهێنان دەکێشین، ڕووکار دیزاین دەکەین، و ئەو فایلانە دەدەینە دەستت کە گەشەپێدەرەکانت لێیان دروست بکەن، یان خۆمان دروستیان دەکەین.',
      },
      {
        title: 'چارەسەری بازرگانی ئەلیکترۆنی',
        description:
          'فرۆشگایەکی ئۆنلاین کە پارە وەردەگرێت، کۆگا بەدواداچوون دەکات و پێت نیشان دەدات چی دەفرۆشرێت. دروستکراوە بۆ شێوازە خۆجێییەکانی پارەدان، نەک تەنها ئەوانەی لە Silicon Valley کار دەکەن.',
      },
      {
        title: 'ئەپی مرۆیی و خێرخوازی',
        description:
          'ئەپ بۆ ڕێکخراو و دامەزراوە خێرخوازییەکان کە بە بودجەی کەم و لە شوێنی ئینتەرنێتی لاوازدا کار دەکەن. ئێمە بۆ ئەم ناوچەیە دروستمان کردووە، بۆیە دەزانین لە مەیدانەکەدا چی تێک دەچێت.',
      },
      {
        title: 'بەڕێوەبردنی سۆشیاڵ میدیا',
        description:
          'ئێمە هەژمارە کۆمەڵایەتییەکانت بەڕێوە دەبەین: پۆستەکان پلان دەکەین، دەینووسین و بڵاویان دەکەینەوە، بۆ ئەوەی فیدەکەت زیندوو بمێنێتەوە بێ ئەوەی هەموو ڕۆژێک چاودێری بکەیت.',
      },
      {
        title: 'دروستکردنی ناوەڕۆک',
        description:
          'وشە، وێنە و ڤیدیۆ کە لە تۆ دەچن و شتێک دەڵێن. ئێمە ناوەڕۆک دروست دەکەین، تۆش ئیتر سەیری لاپەڕەی سپی ناکەیت.',
      },
      {
        title: 'شیکاری داتا',
        description:
          'داشبۆردێک کە وەڵامی ئەو پرسیارانە دەداتەوە کە بەڕاستی دەیانکەیت (چی فرۆشرا، چی وەستا، دواتر چی بکەین)، ڕاستەوخۆ لە سیستەمەکانی خۆتەوە دەهێنرێت.',
      },
    ],
  },
  mission: {
    eyebrow: 'ئامانجمان',
    heading:
      'Datacode ئەپی وێب، ئامرازی زیرەکی دەستکرد و بەرهەمی مۆبایل دیزاین و دروست دەکات. لە کوردستانەوە دروست دەکرێت و بۆ هەموو جیهان دەنێردرێت.',
    body: 'ئێمە ئەپی وێب، ئامرازی زیرەکی دەستکرد و بەرهەمی مۆبایل دروست دەکەین بۆ کڕیارانی ئێرە لە کوردستان و لە سەرانسەری جیهان. پرۆژەیەک لە یەکەم کێشانەوە تا بەرهەم دەگەیەنین، و ئەو کەسانەی دیزاینی دەکەن هەمان ئەو کەسانەن کە کۆدەکە دەنووسن. بێ ڕادەستکردنی کار، بێ ناوبژیوان.',
  },
  clients: {
    eyebrow: 'کڕیارەکانمان',
    heading: 'بەرهەم دروست دەکەین کە کڕیارەکانمان شانازی پێوە دەکەن ناوی خۆیانی لەسەر دابنێن.',
    empty: 'هیچ کڕیارێک بەردەست نییە',
  },
  cta: {
    prompt: 'ئامادەیت پرۆژەیەک دەست پێ بکەیت؟',
    word: 'پەیوەندی',
  },
};

export const aboutCopy = defineCopy(en, ar, ckb);
